import React, { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';

const HostSelector = ({ onHostSelect, selectedHost, placeholder = "Search for a host..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hosts, setHosts] = useState([]);
  const [filteredHosts, setFilteredHosts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Load all hosts on component mount
  useEffect(() => {
    loadHosts();
  }, []);

  // Filter hosts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredHosts(hosts.slice(0, 10)); // Show first 10 hosts
    } else {
      const filtered = hosts.filter(host => 
        host.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        host.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredHosts(filtered.slice(0, 10));
    }
  }, [searchTerm, hosts]);

  const loadHosts = async () => {
    try {
      setLoading(true);
      
      // Get all users and filter locally to avoid index requirements
      const usersQuery = query(
        collection(db, 'users'),
        limit(200)
      );
      
      const snapshot = await getDocs(usersQuery);
      const allUsers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter for hosts only
      const hostsList = allUsers.filter(user => user.role === 'host');
      
      // Sort locally by fullName
      const sortedHosts = hostsList.sort((a, b) => {
        const nameA = (a.fullName || a.email || '').toLowerCase();
        const nameB = (b.fullName || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setHosts(sortedHosts);
      setFilteredHosts(sortedHosts.slice(0, 10));
    } catch (error) {
      console.error('Error loading hosts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHostSelect = (host) => {
    setSearchTerm(host.fullName || host.email);
    setShowDropdown(false);
    onHostSelect(host);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  const clearSelection = () => {
    setSearchTerm('');
    setShowDropdown(false);
    onHostSelect(null);
  };

  return (
    <div className="relative">
      <div className="flex">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="flex-1 bg-gray-700 border border-gray-600 rounded-l px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
        {selectedHost && (
          <button
            onClick={clearSelection}
            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-r transition-colors duration-200"
            title="Clear selection"
          >
            ✕
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-center text-gray-400">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto mb-2"></div>
              Loading hosts...
            </div>
          ) : filteredHosts.length > 0 ? (
            filteredHosts.map((host) => (
              <button
                key={host.id}
                onClick={() => handleHostSelect(host)}
                className="w-full text-left px-4 py-3 hover:bg-gray-700 transition-colors duration-200 border-b border-gray-700 last:border-b-0"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {host.profileImageUrl ? (
                      <img
                        src={host.profileImageUrl}
                        alt={host.fullName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-sm text-gray-300">
                        👤
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">
                      {host.fullName || 'Unnamed Host'}
                    </div>
                    <div className="text-gray-400 text-sm truncate">
                      {host.email}
                    </div>
                    {host.location && (
                      <div className="text-gray-500 text-xs truncate">
                        📍 {host.location}
                      </div>
                    )}
                  </div>
                  {host.gamesHosted && (
                    <div className="text-xs text-green-400 bg-green-900 px-2 py-1 rounded">
                      {host.gamesHosted} games
                    </div>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div className="p-4 text-center text-gray-400">
              {searchTerm ? 'No hosts found matching your search' : 'No hosts available'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HostSelector; 